import { EventSource } from 'global';

import Poller from '../../utils/poller';
import MetadataParser from './metadata-parser';

const ES_SUPPORTED = typeof EventSource !== 'undefined';

/**
 * Opens polling connections to AdsWizz metadata endpoints
 */
export default class MetadataPoller {
  static get defaultOptions () {
    return {
      url: null,
      withCredentials: true,
      parser: new MetadataParser()
    };
  }

  constructor (options) {
    options = Object.assign({}, MetadataPoller.defaultOptions, options);

    this._url = options.url;
    this._started = false;
    this._eventSource = null;
    this._withCredentials = options.withCredentials;
    this._subscribers = [];
    this._parser = options.parser;
    this._logger = options.logger;

    const fallbackOptions = {
      url: options.url,
      withCredentials: options.withCredentials
    };

    if (options.interval) {
      fallbackOptions.interval = options.interval;
    }

    this._poller = new Poller(fallbackOptions);
    this._poller.subscribe(this._logger.wrap((err, response) => {
      if (err) {
        this._notify(err, null);
      } else {
        response.text()
          .then(text => { this._messageHandler(text); });
      }
    }));
  }

  /**
   * Start polling for new metadata entries
   */
  start () {
    if (this._started) return;

    this._started = true;

    if (!ES_SUPPORTED) {
      this._logger.debug('AdsWizzMetadata: EventSource not supported on this browser, falling back to long-polling');
      this._poller.start();
      return;
    }

    const es = this._eventSource = new EventSource(this._url, {
      withCredentials: this._withCredentials
    });

    // called when the EventSource is connected for the first time
    const onConnect = this._logger.wrap((evt) => {
      es.removeEventListener('open', onConnect);
      es.removeEventListener('error', onConnectError);

      this._logger.debug(`AdsWizzMetadata: Established EventSource connection to "${this._url}"`);
    });

    // called when the initial connection attemp fails
    const onConnectError = this._logger.wrap((evt) => {
      es.removeEventListener('open', onConnect);
      es.removeEventListener('error', onConnectError);
      es.close();

      this._logger.debug(`AdsWizzMetadata: Unable to establish EventSource connection to "${this._url}", falling back to long-polling`);
      this._poller.start();
    });

    // called when a new message is received from the EventSource
    const onMessage = this._logger.wrap((evt) => {
      this._messageHandler(evt.data);
    });

    es.addEventListener('open', onConnect);
    es.addEventListener('message', onMessage);
    es.addEventListener('error', onConnectError);
  }

  /**
   * Stop polling for new metadata entries
   */
  stop () {
    if (this._eventSource) {
      this._eventSource.close();
      this._eventSource = null;
      this._logger.debug(`AdsWizzMetadata: Closed EventSource connection to "${this._url}"`);
    }
    this._poller.stop();
    this._started = false;
  }

  /**
   * Add a subscriber to the list
   *
   * @param  {Function} callback Callback to execute when new entries are received
   * @param  {mixed}    context  The scope to call the callback on
   * @return {integer}           The index of the new subscriber in the subscriber list
   */
  subscribe (callback, context = this) {
    return this._subscribers.push({ callback, context }) - 1;
  }

  /**
   * Change the metadata url being polled
   * @param {String} url
   */
  setUrl (url) {
    if (this._started) {
      this.stop();
    }
    this._url = url;
    this._poller.setUrl(url);
    this.start();
  }

  /**
   * Notify subscribers of new entries
   *
   * @param  {Object|null} err  An error if one was encountered during the polling step
   * @param  {Array}       data The list of metadata entries received during the polling step
   */
  _notify (err, data) {
    this._subscribers.forEach(function (subscriber) {
      subscriber.callback.call(subscriber.context, err, data);
    });
  }

  /**
   * Handle incoming raw metadata entries
   *
   * @param  {string} data The raw metadata entries
   */
  _messageHandler (data) {
    const metadataList = this._parser.parse(data);

    // remove duplicate entries from the list
    const metadataListDeduped = [];
    for (let i = 0; i < metadataList.length; i++) {
      const prev = metadataListDeduped[metadataListDeduped.length - 1];
      const curr = metadataList[i];

      if (!prev) {
        metadataListDeduped.push(curr);
        continue;
      }

      // compare actions
      if (prev.action && prev.action === curr.action) {
        continue;
      }

      // compare metadata
      if (prev.metadata && curr.metadata) {
        // compare ads
        if (prev.metadata.adId && prev.metadata.adId === curr.metadata.adId) {
          continue;
        }

        if (prev.metadata.title === curr.metadata.title) {
          continue;
        }
      }

      metadataListDeduped.push(curr);
    }

    if (metadataListDeduped.length > 0) {
      this._logger.debug('AdsWizzMetadata: Received metadata entries');
      this._notify(null, metadataListDeduped);
    }
  }
}
